"use client";

import { GeistSans } from "geist/font/sans";
import type { CSSProperties, ReactElement } from "react";
import "./globals.css";

// This boundary replaces the root layout when it fires, so it cannot rely on
// the Chakra provider from providers.tsx. Everything below is plain markup
// styled with the same design tokens the rest of the app uses.

const pageStyle: CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 24px",
  background: "var(--color-background)",
};

const stackStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  maxWidth: "520px",
  width: "100%",
  textAlign: "center",
};

const eyebrowStyle: CSSProperties = {
  fontSize: "13px",
  fontWeight: 600,
  color: "var(--color-error)",
  letterSpacing: "var(--letter-spacing-label)",
  textTransform: "uppercase",
};

const headingStyle: CSSProperties = {
  fontSize: "32px",
  fontWeight: 600,
  color: "var(--color-text-primary)",
  letterSpacing: "var(--letter-spacing-tight)",
};

const bodyStyle: CSSProperties = {
  fontSize: "16px",
  lineHeight: 1.6,
  color: "var(--color-text-secondary)",
};

const buttonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: "44px",
  padding: "0 24px",
  border: "none",
  borderRadius: "var(--radius-sm)",
  background: "var(--color-primary)",
  color: "white",
  fontSize: "14px",
  fontWeight: 500,
  fontFamily: "inherit",
  cursor: "pointer",
  transition: "var(--transition-fast)",
};

const digestStyle: CSSProperties = {
  fontSize: "12px",
  color: "var(--color-text-muted)",
};

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): ReactElement {
  return (
    <html lang="en" suppressHydrationWarning className={GeistSans.className}>
      <body>
        <div style={pageStyle}>
          <div style={stackStyle}>
            <p style={eyebrowStyle}>Something went wrong</p>
            <h1 style={headingStyle}>OTAlytics could not load</h1>
            <p style={bodyStyle}>
              An unexpected error stopped the app from starting. The problem is on
              our side, not yours, and your tracking data is unaffected. Reload to
              try again.
            </p>
            <button type="button" onClick={reset} style={buttonStyle}>
              Reload OTAlytics
            </button>
            {error.digest && <p style={digestStyle}>Reference code: {error.digest}</p>}
          </div>
        </div>
      </body>
    </html>
  );
}
